/**
 * Floating gear in the bottom-right of every authed page. Opens a
 * mini drawer with palette + fontset toggles so users can re-skin
 * without leaving /app/options. Esc closes; click-outside closes;
 * focus is trapped inside while open.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useVisualSettings } from '../../hooks/useVisualSettings';

const QuickSettings: React.FC = () => {
  const { theme, fontset, setTheme, setFontset } = useVisualSettings();
  const [open, setOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Esc to close; trap focus inside the drawer while open.
  useEffect(() => {
    if (!open) return;
    const drawer = drawerRef.current;
    if (!drawer) return;

    const focusable = drawer.querySelectorAll<HTMLElement>('button, a, [tabindex]:not([tabindex="-1"])');
    focusable[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      } else if (e.key === 'Tab' && focusable.length > 1) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    const onClickOutside = (e: MouseEvent) => {
      if (drawer.contains(e.target as Node)) return;
      if (triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="gv-quicksettings-trigger"
        aria-label="Quick settings"
        aria-expanded={open}
        onClick={() => setOpen(prev => !prev)}
      >
        <span aria-hidden="true">⚙</span>
      </button>
      {open && (
        <div
          ref={drawerRef}
          className="gv-quicksettings-drawer"
          role="dialog"
          aria-modal="false"
          aria-label="Quick settings"
        >
          <div className="gv-qs-section">
            <h4>Theme</h4>
            <div className="gv-qs-row">
              <button type="button" className={`gv-window-btn${theme === 'cobalt' ? ' active' : ''}`} onClick={() => setTheme('cobalt')}>Cobalt</button>
              <button type="button" className={`gv-window-btn${theme === 'plasma' ? ' active' : ''}`} onClick={() => setTheme('plasma')}>Plasma</button>
            </div>
          </div>
          <div className="gv-qs-section">
            <h4>Font</h4>
            <div className="gv-qs-row">
              <button type="button" className={`gv-window-btn${fontset === 'sf' ? ' active' : ''}`} onClick={() => setFontset('sf')}>SF</button>
              <button type="button" className={`gv-window-btn${fontset === 'jetbrains' ? ' active' : ''}`} onClick={() => setFontset('jetbrains')}>JetBrains</button>
            </div>
          </div>
          <Link to="/app/settings" className="gv-qs-more" onClick={() => setOpen(false)}>More settings →</Link>
        </div>
      )}
    </>
  );
};

export default QuickSettings;
